import React, { useState } from "react";
import startCase from "lodash/startCase";
import { formatINR,sumField,safeArray } from "egov-ui-kit/utils/commons";

const buildWorkOrders = (receipt) => receipt.accountHead || [];
const statusClassMap = {
  Active: "status-active",
  "In Progress": "status-in-progress",
  "In Active": "status-inactive", // API sends "In Active" (with a space), not "Inactive"
};

const statusColorMap = {
  Active: { color: "#1a7f37", background: "#dafbe1", border: "1px solid #b4f0c4" },
  "In Progress": { color: "#9a6700", background: "#fff8c5", border: "1px solid #f0dd8f" },
  "In Active": { color: "#6e7781", background: "#eef0f2", border: "1px solid #d8dee4" },
};

const StatusBadge = ({ status }) => (
  <span
    className={`status-badge ${statusClassMap[status] || ""}`}
    style={{
      ...(statusColorMap[status] || {}),
      padding: "2px 10px",
      borderRadius: "999px",
      fontSize: "0.85em",
      fontWeight: 600,
      display: "inline-block",
      whiteSpace: "nowrap",
    }}
  >
    {status}
  </span>
);

// ─────────────────────────────────────────────
// WORK ORDER DETAIL TABLE (3rd level drill-down)
// ─────────────────────────────────────────────
const STATUS_OPTIONS = ["All", "Active", "In Active"];

const WorkOrderDetailTable = ({ receipt, financialYear, ulbName, onBack }) => {
  const workOrders = buildWorkOrders(receipt);
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredWorkOrders =
    statusFilter === "All" ? workOrders : workOrders.filter((w) => w.status === statusFilter);

  const totalValue = sumField(filteredWorkOrders, "workOrderAmount");

  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          <button className="btn btn-secondary back-button" onClick={onBack}>
            Back
          </button>
          <div>
            <h2>{startCase(receipt.contractorName)}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {financialYear}</span>
          <span>|</span>
          <span>ULB : {ulbName}</span>
        </div>
      </div>

      {/* STATUS FILTER */}
      <div
        className="status-filter-bar"
        style={{ display: "flex", alignItems: "center", gap: "8px", margin: "12px 0" }}
      >
        <label htmlFor="statusFilter" style={{ fontWeight: 600 }}>
          Status :
        </label>
        <select
          id="statusFilter"
          className="form-control"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: "auto", display: "inline-block" }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      {/* DETAIL TABLE */}
      <div className="table-responsive">
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>Account Name</th>
              <th>Account Code</th>
              <th>Work Order Date</th>
              <th>Work Order Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkOrders.length > 0 ? (
              <React.Fragment>
                {filteredWorkOrders.map((w) => (
                  <tr key={w.id}>
                    <td>{w.accountHeadName}</td>
                    <td>{w.accountCode}</td>
                    <td>{w.workOrderDate}</td>
                    <td>{formatINR(w.workOrderAmount)}</td>
                    <td>
                      <StatusBadge status={w.status} />
                    </td>
                  </tr>
                ))}
                <tr className="total-row" style={{ fontWeight: "bold" }}>
                  <td colSpan="3">Total</td>
                  <td>{formatINR(totalValue)}</td>
                  <td></td>
                </tr>
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  No Work Order Records Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ReceiptDetailTable = ({ item, financialYear, ulbName, onBack }) => {
  const receipts = safeArray(item.contractorReceipts);
  const [selectedWorkOrderReceipt, setSelectedWorkOrderReceipt] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const statusOptions = ["All", ...Array.from(new Set(receipts.map((r) => r.status).filter(Boolean)))];

  const filteredReceipts = statusFilter === "All" ? receipts : receipts.filter((r) => r.status === statusFilter);

  const totalOrders = filteredReceipts.reduce((acc, r) => acc + Number(r.totalWorkOrders || 0), 0);
  const totalAmount = filteredReceipts.reduce((acc, r) => acc + Number(r.amount || 0), 0);
  const sortedReceipts = [...filteredReceipts].sort((a, b) => {
    const aAmount = Number(a.amount || 0);
    const bAmount = Number(b.amount || 0);
    return bAmount - aAmount; // higher amount first; 0s sink to the bottom
  });
  if (selectedWorkOrderReceipt) {
    return (
      <WorkOrderDetailTable
        receipt={selectedWorkOrderReceipt}
        financialYear={financialYear}
        ulbName={ulbName}
        onBack={() => setSelectedWorkOrderReceipt(null)}
      />
    );
  }

  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          <button className="btn btn-secondary back-button" onClick={onBack}>
            Back
          </button>
          <div>
            <h2>{item.name}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {financialYear}</span>
          <span>|</span>
          <span>ULB : {ulbName}</span>
        </div>
      </div>

      {/* STATUS FILTER */}
      {
        item.contractorReceipts &&  <div
        className="status-filter-bar"
        style={{ display: "flex", alignItems: "center", gap: "8px", margin: "12px 0" }}
      >
        <label htmlFor="contractorStatusFilter" style={{ fontWeight: 600 }}>
          Status :
        </label>
        <select
          id="contractorStatusFilter"
          className="form-control"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: "auto", display: "inline-block" }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
      }
     

      {/* DETAIL TABLE */}
      <div className="table-responsive">
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>Contractor Name</th>
              <th>Total Number of Work Orders</th>
              <th>Total Work Orders Amount</th>
              {/* <th>Status</th> */}
            </tr>
          </thead>
          <tbody>
            {sortedReceipts.length > 0 ? (
              <React.Fragment>
                {sortedReceipts.map((r) => (
                  <tr key={r.id}>
                    <td>{startCase(r.contractorName)}</td>
                    <td>{r.totalWorkOrders}</td>
                    <td>
                      {r.amount > 0 ? (
                        <a
                          href="#"
                          className="link-cell"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedWorkOrderReceipt(r);
                          }}
                        >
                          {formatINR(r.amount)}
                        </a>
                      ) : (
                        formatINR(r.amount)
                      )}
                    </td>
                    {/* <td>
                      <StatusBadge status={r.status} />
                    </td> */}
                  </tr>
                ))}
                <tr className="total-row" style={{ fontWeight: "bold" }}>
                  <td colSpan="1">Total</td>
                  <td>{totalOrders}</td>
                  <td>{formatINR(totalAmount)}</td>
                  {/* <td></td> */}
                </tr>
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                  No Contractor Records Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ContractorTable = ({ contractorDetails }) => {
  const tableData = contractorDetails && contractorDetails;
  const totalContractors = safeArray(tableData.ulbs).reduce((acc, ulb) => acc + (Number(ulb.totalContractor) || 0), 0);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  console.log("selectedReceipt", selectedReceipt);
  if (selectedReceipt) {
    return (
      <ReceiptDetailTable
        item={selectedReceipt}
        financialYear={contractorDetails.financialYear}
        ulbName={contractorDetails.ulbName}
        onBack={() => setSelectedReceipt(null)}
      />
    );
  }

  const currentRows = safeArray(tableData.ulbs);
  const totalsRow = tableData.totals;

  const handleDrillDown = (item) => {
    setSelectedReceipt(item);
  };

  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          <div className="icon-wrapper">
            <i className="material-icons">description</i>
          </div>
          <div>
            <h2>ULB Wise No of Contractor</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {tableData.financialYear}</span>
          <span>|</span>
          <span>ULB : {tableData.ulbName}</span>
        </div>
      </div>

      {/* MAIN DRILL DOWN TABLE */}
      <div className="table-responsive">
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>ULB Name</th>
              <th>Total Contractors</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              <React.Fragment>
                {currentRows.map(function (item) {
                  return (
                    <tr key={item.id}>
                      <td>{item.name}</td>

                      <td>
                        {item.totalContractor > 0 ? (
                          <a
                            href="#"
                            className="link-cell"
                            onClick={function (e) {
                              e.preventDefault();
                              handleDrillDown(item);
                            }}
                          >
                            {formatINR(item.totalContractor)}
                          </a>
                        ) : (
                          formatINR(item.totalContractor)
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr className="total-row">
                  <td>Total</td>
                  <td>{formatINR(totalContractors)}</td>
                </tr>
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan="2" style={{ textAlign: "center" }}>
                  No Record Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContractorTable;


// WEBPACK FOOTER //
// src/modules/employee/Home/ContractorTable.js